import React from "react";
import { HeroSpotlightBackgroundEffects } from "components/hero";
import { Section } from "components/section";
import { TextTitle } from "ui/text";
import { StackedLogos } from "components/logos";
import { desktopOnlyDisplay, type LandingPageProps } from "utils";
import { type CaseStudySimple } from "lib/sanity";
import { AspectRatio, Box, Flex, Icon } from "@chakra-ui/react";
import { Button } from "ui/button";
import { DecorativeBackgroundImage } from "components/images";
import Image from "next/image";
import { FadeIn } from "ui/animations";

type Props = {
  title: string;
  description: string;
  caseStudy?: CaseStudySimple;
} & Pick<LandingPageProps, "defaultLogos" | "defaultMobileLogos">;

export function FeaturedCaseStudyHero({ title, description, caseStudy, defaultLogos, defaultMobileLogos }: Props) {
  const bgColor = "new.violet.950";
  const spotlightColor = "new.violet.900";

  return (
    <Section
      pos="relative"
      theme="dark"
      bg={bgColor}
      themeBgColor={bgColor}
      py="0"
      pt={{ base: "biggest", md: "huge" }}
      pb="biggest"
      overflow="hidden"
    >
      <HeroSpotlightBackgroundEffects theme="dark" bgColor={bgColor} spotlightColor={spotlightColor} />

      <FadeIn>
        <Box pos="relative">
          <TextTitle
            theme="dark"
            size="xl"
            descriptionMaxW="3xl"
            align="center"
            titleAs="h1"
            title={title}
            description={description}
          />

          {caseStudy && (
            <AspectRatio
              ratio={{ base: 350 / 490, md: 16 / 9 }}
              borderRadius={{ base: "small", md: "bigger" }}
              maxH={{ base: "490px", md: "unset" }}
              my="biggest"
              boxShadow="xl"
              overflow="hidden"
            >
              <Flex boxSize="100%">
                <Flex
                  pos="relative"
                  boxSize="100%"
                  flexDir="column"
                  justifyContent="space-between"
                  p={{ base: "small", md: "48px" }}
                >
                  <DecorativeBackgroundImage src={caseStudy.fullImageUrl} />

                  <GreenSpotlight />
                  <VioletSpotlight />

                  <AspectRatio pos="relative" ratio={160 / 60} width="160px" maxH="60px">
                    <Image
                      priority
                      width="160"
                      height="60"
                      src={caseStudy.customer.logoMonoUrl ?? caseStudy.customer.logoUrl}
                      alt={`${caseStudy.customer.name} logo.`}
                      style={{
                        objectFit: "contain",
                        objectPosition: "left",
                        filter:
                          "brightness(0) invert(1) drop-shadow(0px 0px 15px rgba(0, 0, 0, 0.4)) drop-shadow(0px 0px 20px rgba(0, 0, 0, 0.1)) drop-shadow(0px 0px 40px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  </AspectRatio>

                  <TextTitle
                    pos="relative"
                    theme="dark"
                    size="md"
                    maxW="md"
                    title={
                      <Box filter="drop-shadow(0px 0px 10px rgba(34, 132, 87, 0.7)) drop-shadow(0px 0px 20px rgba(34, 132, 87, 0.4)) drop-shadow(0px 0px 40px rgba(34, 132, 87, 0.2)) drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.2))">
                        {caseStudy.title}
                      </Box>
                    }
                    actions={
                      <Button hasRightChevron href={`/customers/${caseStudy.customer.slug}`}>
                        Read the story
                      </Button>
                    }
                  />
                </Flex>
              </Flex>
            </AspectRatio>
          )}

          <StackedLogos
            hasLink={false}
            theme="dark"
            themeBgColor={bgColor}
            title=""
            customers={defaultLogos}
            mobileCustomers={defaultMobileLogos}
            my="0"
          />
        </Box>
      </FadeIn>
    </Section>
  );
}

function GreenSpotlight() {
  return (
    <Icon
      pos="absolute"
      left="0"
      bottom={{ base: "-75px", lg: "0" }}
      width="1320"
      height="780"
      viewBox="0 0 1320 780"
      fill="none"
    >
      <g filter="url(#hero-green-spotlight-b)" clipPath="url(#hero-green-spotlight-a)">
        <Box as="circle" cx="128.189" cy="823.508" r="456.269" fill="new.green.700" />
      </g>
      <defs>
        <clipPath id="hero-green-spotlight-a">
          <path fill="white" d="M0 0h1320v780H0z" />
        </clipPath>
        <filter
          id="hero-green-spotlight-b"
          x="-728.08"
          y="-32.761"
          width="1712.54"
          height="1712.54"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_366_706" />
        </filter>
      </defs>
    </Icon>
  );
}

function VioletSpotlight() {
  return (
    <Icon
      pos="absolute"
      top="0"
      right="0"
      bottom="0"
      width="auto"
      height="auto"
      viewBox="0 0 1320 780"
      fill="none"
      display={desktopOnlyDisplay()}
    >
      <g filter="url(#hero-violet-spotlight-b)" clipPath="url(#hero-violet-spotlight-a)">
        <Box as="circle" cx="1435.37" cy="-152.829" r="456.269" fill="new.violet.700" />
      </g>
      <defs>
        <clipPath id="hero-violet-spotlight-a">
          <path fill="white" d="M0 0h1320v780H0z" />
        </clipPath>
        <filter
          id="hero-violet-spotlight-b"
          x="579.103"
          y="-1009.1"
          width="1712.54"
          height="1712.54"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_369_715" />
        </filter>
      </defs>
    </Icon>
  );
}
